import { createCommitmentRoutes } from "features/commitments/CommitmentRoutes";
import { getCapitalActivityTableDataRetrieveLoader } from "gen/endpoints/capital-activity/capital-activity";
import { getCommitmentInformationTableDataByFundRetrieveLoader } from "gen/endpoints/commitment-information-table-data-by-fund/commitment-information-table-data-by-fund";
import { getCommitmentStatsRetrieveLoader } from "gen/endpoints/commitment-stats/commitment-stats";
import { getFeesAndCarryRetrieveLoader } from "gen/endpoints/fees-and-carry/fees-and-carry";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getInvestorEntityRetrieveLoader } from "gen/endpoints/investor-entity/investor-entity";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const createInvestorEntityRoutes = (parentId?: string): RouteObject => {
  const idPrefix = parentId
    ? `${parentId}InvestorEntities`
    : "investorEntities";
  return {
    id: idPrefix,
    path: "investor-entities",
    children: [
      {
        id: `${idPrefix}InvestorEntity`,
        path: ":investorEntityId",
        lazy: async () => {
          const { InvestorEntityPage } = await import(
            "features/investor-entities/InvestorEntityPage"
          );
          return { Component: InvestorEntityPage };
        },
        loader: composeLoaders(
          getInvestorEntityRetrieveLoader(({ investorEntityId }) => ({
            id: investorEntityId!,
          })),
          getFundListLoader(({ investorEntityId }) => ({
            investor_entity_id: investorEntityId!,
          })),
          getFeesAndCarryRetrieveLoader(({ investorEntityId }) => ({
            investor_entity_id: investorEntityId!,
          })),
          getCommitmentStatsRetrieveLoader(({ investorEntityId }) => ({
            investor_entity_id: investorEntityId!,
          })),
        ),
        children: [
          {
            id: `${idPrefix}InvestorEntityTabActivity`,
            path: "activity",
            lazy: async () => {
              const { InvestorEntityTabActivity } = await import(
                "features/investor-entities/tabs/InvestorEntityTabActivity"
              );
              return { Component: InvestorEntityTabActivity };
            },
            loader: composeLoaders(
              getCommitmentStatsRetrieveLoader(({ investorEntityId }) => ({
                investor_entity_id: investorEntityId!,
              })),
              getCapitalActivityTableDataRetrieveLoader(
                ({ investorEntityId }) => ({
                  investor_entity_id: investorEntityId!,
                }),
              ),
            ),
            handle: {
              title: i18n.t("investorEntity:tabs.activity.title"),
            },
          },
          {
            id: `${idPrefix}InvestorEntityTabProfile`,
            path: "profile",
            lazy: async () => {
              const { InvestorEntityTabProfile } = await import(
                "features/investor-entities/tabs/profile/InvestorEntityTabProfile"
              );
              return { Component: InvestorEntityTabProfile };
            },
            handle: {
              title: i18n.t("investorEntity:tabs.profile.title"),
            },
          },
          {
            id: `${idPrefix}InvestorEntityTabCommitments`,
            path: "commitments",
            lazy: async () => {
              const { InvestorEntityTabCommitments } = await import(
                "features/investor-entities/tabs/InvestorEntityTabCommitments"
              );
              return { Component: InvestorEntityTabCommitments };
            },
            loader: getCommitmentInformationTableDataByFundRetrieveLoader(
              ({ investorEntityId }) => ({
                investor_entity_id: investorEntityId!,
              }),
            ),
            handle: {
              title: i18n.t("investorEntity:tabs.commitments.title"),
            },
          },
          {
            id: `${idPrefix}InvestorEntityTabPerformance`,
            path: "performance",
            lazy: async () => {
              const { HypotheticalPerformanceTab } = await import(
                "features/financial-reporting/HypotheticalPerformanceTab"
              );
              return { Component: HypotheticalPerformanceTab };
            },
            handle: {
              title: i18n.t("investorEntity:tabs.performance.title"),
            },
          },
        ],
      },
      ...(!idPrefix.includes("InvestorEntityCommitments")
        ? [
            createCommitmentRoutes(
              `${idPrefix}InvestorEntity`,
              ":investorEntityId/commitments",
              false,
            ),
          ]
        : []),
    ],
  };
};
